import {
  ArrowLeft,
  SemanticBadge,
  Skeleton,
  ThemeColorNamesType,
  getColorByName,
} from "@bale/ui";
import { strings } from "intl/fa";
import React, {
  ReactComponentElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import styles from "../../campaignOrders/campaignItem/CampaignItem.module.scss";
import sharedStyles from "../../campaignOrders/CampaignOrders.module.scss";
import {
  AdSpot,
  AdState,
  ApiAdRecord,
} from "../../../../../../../core/orderAd/orderAd.entity";
import { useAppDispatch } from "../../../../../../../utils/store";
import {
  NavigationActions,
  AppPage,
} from "../../../../../../../core/navigation/navigation.slice";
import { OrdersListActions } from "../../../../../../../core/ordersList/ordersList.slice";
import { useRequest } from "../../../../../../../hooks/useRequest";
import { getAdReportThunk } from "../../../../../../../core/ordersList/ordersList.thunk";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import {
  Close2,
  History,
  IconComponent,
  MidSync,
  Money,
  Show,
  Tick,
} from "@bale/icons";
import useIsLargeScreen from "../../../hooks/useIsDesktop";
import AdThumbnail from "../../AdThumbnail";

export const adStateLabels: {
  [key: string]: {
    label: string;
    color: ThemeColorNamesType;
    icon?: IconComponent;
    size?: number;
  };
} = {
  [AdState.Finished]: {
    label: strings.campaign_labels.Finished,
    color: "color_neutrals_n_300",
  },
  [AdState.AwaitingContentApproval]: {
    label: strings.campaign_labels.AwaitingApproval,
    color: "color_alternative_a_01",
    icon: MidSync,
    size: 16,
  },
  [AdState.Active]: {
    label: strings.campaign_labels.Active,
    color: "color_alternative_a_09",
    icon: Show,
    size: 16,
  },
  [AdState.Rejected]: {
    label: strings.campaign_labels.Rejected,
    color: "color_error",
    icon: Close2,
    size: 16,
  },
  [AdState.Canceled]: {
    label: strings.campaign_labels.Canceled,
    color: "color_alternative_a_04",
  },
  [AdState.Unknown]: {
    label: strings.ad_states_labels.Unknown,
    color: "color_neutrals_n_300",
  },
  [AdState.AwaitingPayment]: {
    label: strings.ad_states_labels.AwaitingPayment,
    color: "color_alternative_a_02",
    icon: Money,
  },
  [AdState.AwaitingToShow]: {
    label: strings.ad_states_labels.AwaitingToShow,
    color: "color_alternative_a_02",
    icon: History,
  },
};

export const useOpenOrderDetails = () => {
  const dispatch = useAppDispatch();
  const { sendRequest } = useRequest(getAdReportThunk);
  return (adRecord: ApiAdRecord) => {
    dispatch(OrdersListActions.setSelectedAd(adRecord));
    dispatch(NavigationActions.setAppPage(AppPage.OrderDetail));
    if (
      adRecord.state === AdState.Finished ||
      adRecord.state === AdState.Active
    ) {
      sendRequest({ ad_id: adRecord.id });
    }
  };
};

interface Props {
  adRecord: ApiAdRecord;
}

const OrderedAdItem: React.FC<React.PropsWithChildren<Props>> = ({
  adRecord,
}) => {
  const selectAd = useOpenOrderDetails();
  const isDesktop = useIsLargeScreen(1400);

  return isDesktop ? (
    <div
      className={sharedStyles.campaignItem}
      onClick={() => selectAd(adRecord)}
    >
      <div className={sharedStyles.col} style={{ height: 50 }}>
        <AdThumbnail
          pic_url={adRecord.pic_url}
          spot={adRecord.spot}
          id={adRecord.id}
        />
      </div>
      <p className={`${sharedStyles.col} ${styles.title}`}>
        {adRecord.title ? adRecord.title : "-"}
      </p>
      <p className={`${sharedStyles.col} ${styles.desc}`}>
        {adRecord.description}
      </p>
      <p className={sharedStyles.col}>
        <SemanticBadge
          color="color_neutrals_n_300"
          text={
            adRecord.spot === AdSpot.AdSpotSponsoredMessage
              ? strings.ad_sale.ordered_ads_list.ad_type.sponsored_message
              : strings.ad_sale.ordered_ads_list.ad_type.dialog
          }
        />
      </p>
      <div className={sharedStyles.col}>
        <SemanticBadge
          color={adStateLabels[adRecord.state].color}
          text={adStateLabels[adRecord.state].label}
          icon={adStateLabels[adRecord.state].icon}
          iconSize={adStateLabels[adRecord.state].size}
          className={styles.badge}
        />
      </div>
      <p className={sharedStyles.col}>
        {adRecord.view_count === 0
          ? digitsToHindi(toMoneyFormat(String(adRecord.click_count)))
          : digitsToHindi(toMoneyFormat(String(adRecord.view_count)))}
      </p>
      <div className={sharedStyles.col}>
        <ArrowLeft size={24} />
      </div>
    </div>
  ) : (
    <div
      className={sharedStyles.campaignCard}
      onClick={() => selectAd(adRecord)}
    >
      <div className={sharedStyles.header}>
        <div className={sharedStyles.info}>
          <AdThumbnail
            pic_url={adRecord.pic_url}
            spot={adRecord.spot}
            id={adRecord.id}
          />
          <div className={sharedStyles.texts}>
            <h2>
              {adRecord.title
                ? adRecord.title
                : strings.campaign_details.without_title}
            </h2>
            <p>{adRecord.description}</p>
          </div>
        </div>
        <ArrowLeft size={24} />
      </div>

      <div className={sharedStyles.row}>
        <p>{strings.ad_sale.ordered_ads_list.adStatus}:</p>

        <SemanticBadge
          color={adStateLabels[adRecord.state].color}
          text={adStateLabels[adRecord.state].label}
          icon={adStateLabels[adRecord.state].icon}
          iconSize={adStateLabels[adRecord.state].size}
          className={styles.badge}
        />
      </div>

      <div className={sharedStyles.row}>
        <p>{strings.ad_sale.ordered_ads_list.adSpot}:</p>
        <SemanticBadge
          color="color_neutrals_n_300"
          text={
            adRecord.spot === AdSpot.AdSpotSponsoredMessage
              ? strings.ad_sale.ordered_ads_list.ad_type.sponsored_message
              : strings.ad_sale.ordered_ads_list.ad_type.dialog
          }
        />
      </div>
    </div>
  );
};

export default OrderedAdItem;
